import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import api from "@/lib/api";
import { PageHeader, StatCard, Loading } from "@/components/common";
import { fmtMoney, fmtDate } from "@/lib/format";
import { UsersThree, Clock, Money, CalendarDots, ClipboardText, FileText, Cake, Fingerprint } from "@phosphor-icons/react";
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid } from "recharts";

export default function Dashboard() {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const [data, setData] = useState(null);

  useEffect(() => {
    const skip = () => Promise.reject(new Error("admin-only"));
    Promise.allSettled([
      isAdmin ? api.get("/stats") : skip(),
      isAdmin ? api.get("/stats/extended") : skip(),
      isAdmin ? api.get("/birthdays/upcoming", { params: { within_days: 30 } }) : skip(),
      api.get("/attendance/today"),
      isAdmin ? api.get("/payrolls") : skip(),
    ]).then(([stats, ext, bdays, today, payrolls]) => {
      setData({
        stats: stats.status === "fulfilled" ? stats.value.data : {},
        ext: ext.status === "fulfilled" ? ext.value.data : {},
        birthdays: bdays.status === "fulfilled" ? bdays.value.data : [],
        today: today.status === "fulfilled" ? today.value.data : [],
        payrolls: payrolls.status === "fulfilled" ? payrolls.value.data : [],
      });
    });
  }, []);

  if (!data) return <Loading label="Cargando panel..." />;

  const present = data.today.filter((t) => t.status !== "absent" && t.status !== "day_off").length;
  const chartData = data.payrolls
    .slice(0, 10)
    .map((p) => ({ name: p.employee_name?.split(" ")[0] || "—", neto: p.net_pay }))
    .reverse();

  return (
    <div data-testid="dashboard-page">
      <PageHeader title="Panel general" description="Resumen en tiempo real de tu operación de recursos humanos" testid="dashboard-header" />

      {isAdmin && (
      <>
      <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-6 gap-4">
        <StatCard icon={UsersThree} label="Empleados" value={data.stats.employees ?? 0} testid="stat-employees" />
        <StatCard icon={Clock} label="Turnos" value={data.stats.shifts ?? 0} testid="stat-shifts" />
        <StatCard icon={CalendarDots} label="Turnos hoy" value={data.stats.today_shifts ?? 0} testid="stat-today-shifts" />
        <StatCard icon={Fingerprint} label="Asistencia hoy" value={present} hint={`de ${data.today.length} programados`} testid="stat-attendance" />
        <StatCard icon={Money} label="Planillas" value={data.stats.payrolls ?? 0} testid="stat-payrolls" />
        <StatCard icon={ClipboardText} label="Solicitudes pend." value={data.ext.pending_requests ?? 0} testid="stat-pending" />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 mt-4">
        <div className="lg:col-span-2 border border-border bg-card rounded-sm p-5" data-testid="payroll-chart-card">
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-heading text-lg font-bold text-foreground">Pago neto por planilla</h3>
            <Link to="/planillas" className="text-xs text-muted-foreground hover:text-foreground transition-colors duration-200" data-testid="goto-payrolls">
              Ver planillas →
            </Link>
          </div>
          {chartData.length === 0 ? (
            <p className="text-sm text-muted-foreground py-10 text-center">Aún no hay planillas registradas.</p>
          ) : (
            <ResponsiveContainer width="100%" height={260}>
              <BarChart data={chartData} margin={{ left: 8, right: 8 }}>
                <CartesianGrid stroke="hsl(var(--border))" vertical={false} />
                <XAxis dataKey="name" stroke="hsl(var(--muted-foreground))" fontSize={12} tickLine={false} axisLine={false} />
                <YAxis stroke="hsl(var(--muted-foreground))" fontSize={12} tickLine={false} axisLine={false} tickFormatter={(v) => `${(v / 1000).toFixed(0)}k`} />
                <Tooltip
                  cursor={{ fill: "hsl(var(--muted))" }}
                  contentStyle={{ background: "hsl(var(--card))", border: "1px solid hsl(var(--border))", borderRadius: 2 }}
                  labelStyle={{ color: "hsl(var(--card-foreground))" }}
                  formatter={(v) => [fmtMoney(v), "Neto"]}
                />
                <Bar dataKey="neto" fill="hsl(var(--chart-1))" radius={[2, 2, 0, 0]} maxBarSize={40} />
              </BarChart>
            </ResponsiveContainer>
          )}
        </div>

        <div className="border border-border bg-card rounded-sm p-5" data-testid="birthdays-card">
          <h3 className="font-heading text-lg font-bold text-foreground mb-4 flex items-center gap-2">
            <Cake size={18} className="text-muted-foreground" /> Cumpleaños próximos
          </h3>
          {data.birthdays.length === 0 ? (
            <p className="text-sm text-muted-foreground py-6 text-center">Sin cumpleaños en los próximos 30 días.</p>
          ) : (
            <ul className="divide-y divide-border">
              {data.birthdays.slice(0, 6).map((b) => (
                <li key={b.employee_id} className="py-3 flex items-center justify-between" data-testid={`birthday-${b.employee_id}`}>
                  <div>
                    <p className="text-sm text-foreground font-medium">{b.full_name}</p>
                    <p className="text-xs text-muted-foreground">{b.position || "Sin puesto"}</p>
                  </div>
                  <div className="text-right">
                    <p className="text-sm text-foreground/80">
                      {b.day}/{b.month}
                    </p>
                    <p className="text-xs text-muted-foreground">
                      {b.days_until === 0 ? "¡Hoy!" : `en ${b.days_until} días`}
                    </p>
                  </div>
                </li>
              ))}
            </ul>
          )}
          <div className="mt-4 pt-4 border-t border-border flex items-center justify-between text-xs text-muted-foreground">
            <span className="flex items-center gap-1.5">
              <FileText size={13} /> {data.ext.documents ?? 0} documentos generados
            </span>
            <Link to="/documentos" className="hover:text-foreground transition-colors duration-200" data-testid="goto-documents">
              Documentos →
            </Link>
          </div>
        </div>
      </div>

      </>
      )}
      <div className="mt-4 border border-border bg-card rounded-sm p-5" data-testid="today-card">
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-heading text-lg font-bold text-foreground">Asistencia de hoy</h3>
          <Link to="/asistencia" className="text-xs text-muted-foreground hover:text-foreground transition-colors duration-200" data-testid="goto-attendance">
            Ver detalle →
          </Link>
        </div>
        {data.today.length === 0 ? (
          <p className="text-sm text-muted-foreground py-6 text-center">No hay turnos programados para hoy.</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-xs uppercase tracking-widest text-muted-foreground border-b border-border">
                  <th className="pb-2 font-normal">Empleado</th>
                  <th className="pb-2 font-normal">Turno</th>
                  <th className="pb-2 font-normal">Entrada</th>
                  <th className="pb-2 font-normal">Salida</th>
                  <th className="pb-2 font-normal">Horas</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-border">
                {data.today.slice(0, 8).map((t) => (
                  <tr key={t.employee_id} className="hover:bg-muted/70 transition-colors duration-200">
                    <td className="py-2.5 text-foreground">{t.employee_name}</td>
                    <td className="py-2.5 text-muted-foreground">{t.shift_name || "—"}</td>
                    <td className="py-2.5 text-foreground/80">{t.first_in ? new Date(t.first_in).toLocaleTimeString("es-HN", { hour: "2-digit", minute: "2-digit" }) : "—"}</td>
                    <td className="py-2.5 text-foreground/80">{t.last_out ? new Date(t.last_out).toLocaleTimeString("es-HN", { hour: "2-digit", minute: "2-digit" }) : "—"}</td>
                    <td className="py-2.5 text-foreground/80">{t.worked_hours?.toFixed(1) ?? "0.0"}h</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
      <p className="mt-6 text-xs text-muted-foreground">Datos sincronizados con CAUS - HR Suite · {fmtDate(new Date().toISOString())}</p>
    </div>
  );
}
